
import React, { createContext, useContext, useState, ReactNode } from "react";

export type ConversionStage = 
  | "hardened" 
  | "skeptical" 
  | "curious" 
  | "convicted" 
  | "ready" 
  | "converted";

interface EvangelismAssistantContextType {
  conversionStage: ConversionStage;
  setConversionStage: (stage: ConversionStage) => void;
  conversionProgress: number;
  softeningScore: number;
  setSofteningScore: (score: number) => void;
  messages: Array<{ id: string; sender: "user" | "ai"; text: string }>;
  addMessage: (sender: "user" | "ai", text: string) => void;
  resetChat: () => void;
}

const stageToProgress = {
  hardened: 0,
  skeptical: 20,
  curious: 40,
  convicted: 60,
  ready: 80,
  converted: 100
};

const EvangelismAssistantContext = createContext<EvangelismAssistantContextType | undefined>(undefined);

export const useEvangelismAssistant = () => {
  const context = useContext(EvangelismAssistantContext);
  if (context === undefined) {
    throw new Error("useEvangelismAssistant must be used within an EvangelismAssistantProvider");
  }
  return context;
};

export const EvangelismAssistantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [conversionStage, setConversionStage] = useState<ConversionStage>("hardened");
  const [softeningScore, setSofteningScore] = useState(0);
  const [messages, setMessages] = useState<Array<{ id: string; sender: "user" | "ai"; text: string }>>([]);

  const conversionProgress = stageToProgress[conversionStage];

  const addMessage = (sender: "user" | "ai", text: string) => {
    const newMessage = {
      id: Date.now().toString(),
      sender,
      text
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  };

  const resetChat = () => {
    setMessages([]);
    setConversionStage("hardened");
    setSofteningScore(0);
  };

  const value = {
    conversionStage,
    setConversionStage,
    conversionProgress,
    softeningScore,
    setSofteningScore,
    messages,
    addMessage,
    resetChat
  };

  return (
    <EvangelismAssistantContext.Provider value={value}>
      {children}
    </EvangelismAssistantContext.Provider>
  );
};
